// B-46 — a spreadsheet drawn in the browser, for the one case Microsoft's viewer cannot open:
// a document served by a developer's own backend (http://localhost), which Microsoft's servers
// cannot reach. The bytes are the same ones the extraction read; SheetJS (already a dependency)
// parses them here and each sheet is drawn as a plain table.
//
// Rendered through React, cell by cell — never `sheet_to_html` + `dangerouslySetInnerHTML` —
// so a cell holding markup is shown as text. Capped, because a month of rows is fine and a
// 100.000-row export would freeze the dialog.

import { useEffect, useState } from "react";
import * as XLSX from "xlsx";

const MAX_FILAS = 500;

export function VistaHojaDeCalculo({ url, nombre }) {
  const [libro, setLibro] = useState(null);
  const [hoja, setHoja] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let vigente = true;
    setLibro(null);
    setError(null);
    fetch(url)
      .then((respuesta) => {
        if (!respuesta.ok) throw new Error(`el servidor respondió ${respuesta.status}`);
        return respuesta.arrayBuffer();
      })
      .then((bytes) => {
        if (!vigente) return;
        const leido = XLSX.read(bytes, { type: "array" });
        setLibro(leido);
        setHoja(leido.SheetNames[0] || null);
      })
      .catch((e) => {
        if (vigente) setError(e.message || "no se pudo abrir el archivo");
      });
    return () => {
      vigente = false;
    };
  }, [url]);

  if (error) {
    return (
      <p className="p-6 text-center text-[12.5px] text-brinks-muted-foreground">
        No se pudo mostrar {nombre || "la hoja de cálculo"}: {error}.
      </p>
    );
  }
  if (!libro) {
    return <p className="p-6 text-center text-[12.5px] text-brinks-muted-foreground">Abriendo hoja de cálculo…</p>;
  }

  const filas = hoja ? XLSX.utils.sheet_to_json(libro.Sheets[hoja], { header: 1, raw: false, blankrows: false }) : [];
  const columnas = filas.reduce((max, fila) => Math.max(max, fila.length), 0);
  const visibles = filas.slice(0, MAX_FILAS);

  return (
    <div className="h-[54vh] w-full overflow-auto rounded-[6px] border bg-card">
      {libro.SheetNames.length > 1 ? (
        <div className="sticky top-0 z-10 flex gap-1 border-b bg-card p-1.5">
          {libro.SheetNames.map((nombreHoja) => (
            <button
              key={nombreHoja}
              type="button"
              onClick={() => setHoja(nombreHoja)}
              className={`rounded-[6px] px-2 py-0.5 text-[11px] ${nombreHoja === hoja ? "bg-brinks-surface font-semibold" : "text-brinks-muted-foreground"}`}
            >
              {nombreHoja}
            </button>
          ))}
        </div>
      ) : null}
      <table className="min-w-full border-collapse text-[11px]">
        <tbody>
          {visibles.map((fila, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={i} className="border-b border-brinks-border">
              <td className="sticky left-0 bg-brinks-surface px-1.5 text-right text-brinks-muted-foreground">{i + 1}</td>
              {Array.from({ length: columnas }, (_, j) => (
                // eslint-disable-next-line react/no-array-index-key
                <td key={j} className="whitespace-nowrap border-l border-brinks-border px-1.5 py-0.5">
                  {fila[j] ?? ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {filas.length > MAX_FILAS ? (
        <p className="p-2 text-[11px] text-brinks-muted-foreground">
          Mostrando {MAX_FILAS} de {filas.length} filas. Descarga el archivo para verlo completo.
        </p>
      ) : null}
    </div>
  );
}
